import {
  lastInstanceIdStore,
  lastPortalIdByInstanceStore,
  portalsLoader,
  useBoot,
  useDashboardFlags,
  useUser
} from '@metorial/state';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

// Everything the BlackOS shell needs to draw the rail, sub-navs and top bar.
// Works in all three areas: the product (/i/:instanceId), settings (/o/:org...)
// and account (/account), where no instance is in the URL. In that case it uses
// the last instance the user had open, or the organization's production instance.

export type BootData = NonNullable<ReturnType<typeof useBoot>['data']>;
export type ShellOrganization = BootData['organizations'][number];
export type ShellProject = BootData['projects'][number];
export type ShellInstance = BootData['instances'][number];

export type ShellPortal = {
  id: string;
  name: string;
  slug: string;
  urls: { type: string; url: string }[];
  magicMcpUrl: string;
};

let useLastInstanceId = () => {
  let [value, setValue] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    let active = true;
    lastInstanceIdStore
      .get()
      .then(v => active && setValue(v ?? null))
      .catch(() => active && setValue(null));
    return () => {
      active = false;
    };
  }, []);

  return value;
};

let useLastPortalId = (instanceId: string | undefined) => {
  let [value, setValue] = useState<string | null>(null);

  useEffect(() => {
    if (!instanceId) return;
    let active = true;
    lastPortalIdByInstanceStore
      .get(instanceId)
      .then(v => active && setValue(v ?? null))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [instanceId]);

  return value;
};

export let useShellScope = (opts?: { portalId?: string }) => {
  let boot = useBoot();
  let user = useUser();
  let params = useParams<{
    organizationId?: string;
    projectId?: string;
    instanceId?: string;
    portalId?: string;
  }>();
  let lastInstanceId = useLastInstanceId();

  let resolved = useMemo(() => {
    let data = boot.data;
    if (!data) return null;

    let matches = (e: { id: string; slug: string }, key?: string) =>
      !!key && (e.id == key || e.slug == key);

    let organizationFromUrl = data.organizations.find(o => matches(o, params.organizationId));
    let projectFromUrl = data.projects.find(
      p =>
        matches(p, params.projectId) &&
        (!organizationFromUrl || p.organizationId == organizationFromUrl.id)
    );

    let candidates = data.instances.filter(
      i =>
        (!organizationFromUrl || i.organizationId == organizationFromUrl.id) &&
        (!projectFromUrl || i.project.id == projectFromUrl.id)
    );

    let instance =
      candidates.find(i => matches(i, params.instanceId)) ??
      candidates.find(i => i.id == lastInstanceId) ??
      candidates.find(i => i.type == 'production') ??
      candidates[0] ??
      null;

    let organization =
      organizationFromUrl ??
      data.organizations.find(o => o.id == instance?.organizationId) ??
      data.organizations[0] ??
      null;

    let project =
      projectFromUrl ?? data.projects.find(p => p.id == instance?.project.id) ?? null;

    return { organization, project, instance };
  }, [boot.data, params.organizationId, params.projectId, params.instanceId, lastInstanceId]);

  let instance = resolved?.instance ?? null;

  let portals = portalsLoader.use(
    instance ? { instanceId: instance.id, limit: 100, order: 'asc' } : null
  );
  let portalItems: ShellPortal[] = (portals.data?.items ?? []).filter(
    p => p.status == 'active'
  );

  let lastPortalId = useLastPortalId(instance?.id);
  let portalKey = opts?.portalId ?? params.portalId;
  let urlPortal = portalKey
    ? portalItems.find(p => p.id == portalKey || p.slug == portalKey)
    : undefined;
  let defaultPortal =
    urlPortal ??
    portalItems.find(p => p.id == lastPortalId) ??
    // The oldest portal is the one created with the instance.
    portalItems[0] ??
    null;

  let flags = useDashboardFlags();
  let flagValues = (flags.data?.flags ?? null) as Record<string, unknown> | null;

  return {
    boot,
    user,
    organization: resolved?.organization ?? null,
    project: resolved?.project ?? null,
    instance,
    portals: portalItems,
    currentPortal: urlPortal ?? null,
    defaultPortal,
    /** `true`/`false` once flags have loaded, `undefined` before. */
    flag: (name: string) => (flagValues ? !!flagValues[name] : undefined)
  };
};

export type ShellScope = ReturnType<typeof useShellScope>;
